import {
  HR_CATEGORY_ID,
  INCIDENT_CATEGORY_ID,
  ITEM_ID_SEIZURE,
  ITEM_ID_SHORT_SEIZURE,
  SAFEGUARDING_ITEM_ID
} from '@lmc/common/types'
import type { LogCategory, Resident } from '@lmc/ts-common'
import { css, StyleSheet } from 'aphrodite/no-important'
import useFeatureFlags from 'hooks/useFeatureFlags'
import _ from 'lodash'
import { type FC, Suspense, useState } from 'react'
import { useQueryClient } from 'react-query'
import { connect } from 'react-redux'
import { postJSON } from '../../../common/services/api'
import { showToast } from '../../../common/utils'
import { ScrollLock } from '../../../LEGACY/elemental'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import Field from '../../fields'
import { Step, StepList } from '../../generic'
import Modal from '..'
import {
  ModalCategoryItemSelector,
  ModalCategorySelector,
  ModalResidentSelector,
  ModalTitleSelector
} from '../components'
import LogsGoalsSelect from './LogsGoalsSelect'
import LogsSeizureDeEscalation from './LogsSeizureDeEscalation'

enum AddLogStep {
  SU = 0,
  CATEGORY = 1,
  ITEM = 2,
  LOG = 3
}

interface AddLogModalProps {
  categories: LogCategory[]
  categoryItems: any[]
  close: () => void
  createItem: (args: {
    url: string
    json: any
    onComplete: () => void
    onError: () => void
  }) => void
  fetchLogs: () => void
  fields: any // Add the appropriate type
  hasPro: boolean
  isOpen: boolean
  residents: Resident[]
  summary: {
    icon: {
      color: string
      url: string
    }
    text: string
  }
  setFormField: (args: { key: string; value: any }) => void
  taskCompletion?: boolean
  testId?: string
}

const AddLogModal: FC<AddLogModalProps> = ({
  categories,
  categoryItems,
  close,
  hasPro,
  isOpen,
  residents,
  summary,
  taskCompletion,
  testId,
  ...formProps
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const flags = useFeatureFlags({
    flagNames: ['clinical_safety_seizures'] as const
  })?.data

  const checkTitleValid = () => {
    if (!formProps.fields.title || !formProps.fields.description) return false
    return true
  }

  const filterResidentsByStatus = (residents: any[]) =>
    _.filter(residents, (resident) =>
      PERMITTED_RESIDENT_STATUSES.includes(resident.status)
    )

  const getHeaderProps = () => {
    const selectedResident = _.find(residents, {
      id: formProps.fields.resident
    })
    if (!selectedResident) return null

    return {
      isMainHeader: true,
      profilePicture: selectedResident.picture,
      title: `${selectedResident.name.first} ${selectedResident.name.last}`
    }
  }

  const handleTaskCompletionWithLog = async (
    taskId: string,
    taskStatusPath: string,
    taskOnSuccess: (() => void) | undefined,
    logData: any
  ) => {
    try {
      // Step 1: Complete the task (this creates a basic log entry with the note)
      await postJSON({
        method: 'POST',
        url: `list/tasks/${taskId}/${taskStatusPath}`,
        json: {
          note: logData.description || ''
        }
      })

      // Step 2: Create the log with the task ID
      // Backend will find the existing log and merge the data
      formProps.createItem({
        url: 'list/logs',
        json: {
          ...logData,
          task: taskId // Include task ID so backend merges the logs
        },
        onComplete: () => {
          formProps.fetchLogs()
          queryClient.invalidateQueries(['incident-list'])
          showToast({ title: 'Completed To-Do and Added Log' })

          // Call the task's onSuccess callback to refresh task lists
          if (taskOnSuccess) {
            taskOnSuccess()
          }

          setIsLoading(false)
        },
        onError: () => {
          showToast({
            title: 'Task completed but failed to add log details',
            type: 'error'
          })
          setIsLoading(false)
        }
      })
    } catch (error) {
      showToast({
        title: 'Failed to complete To-Do',
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const handleSubmit = () => {
    setIsLoading(true)
    // Extract task completion data if present
    const {
      _taskId: taskId,
      _taskStatusPath: taskStatusPath,
      _taskOnSuccess: taskOnSuccess,
      _taskComment,
      ...logData
    } = formProps.fields

    if (taskId && taskStatusPath) {
      // If this is from a task completion, complete the task first, then create the log
      handleTaskCompletionWithLog(
        taskId,
        taskStatusPath,
        taskOnSuccess,
        logData
      )
    } else {
      // Normal log creation flow (not from task completion)
      formProps.createItem({
        url: 'list/logs',
        json: logData,
        onComplete: () => {
          formProps.fetchLogs()
          queryClient.invalidateQueries(['incident-list'])
          showToast({ title: 'Added a Log' })
          setIsLoading(false)
        },
        onError: () => {
          setIsLoading(false)
        }
      })
    }
  }

  const renderDateTimePicker = () => (
    <div
      className={css(classes.timePickerContainer)}
      data-testid={
        testId ? `${testId}-time-picker` : 'add-log-modal-time-picker'
      }
    >
      <div
        className={css(classes.formField)}
        data-testid={
          testId ? `${testId}-time-field` : 'add-log-modal-time-field'
        }
      >
        <label
          className={css(classes.label)}
          data-testid={
            testId ? `${testId}-time-label` : 'add-log-modal-time-label'
          }
        >
          {TIME}
        </label>
        <div
          className={css(classes.datePickerContainer)}
          data-testid={
            testId
              ? `${testId}-date-picker-container`
              : 'add-log-modal-date-picker-container'
          }
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={MODAL_ID}
              keys="timeLogged"
              layout="column"
              type="datetime"
              values={{
                ...formProps.fields,
                timeLogged: formProps.fields.timeLogged || new Date()
              }}
              data-testid={
                testId
                  ? `${testId}-time-logged-input`
                  : 'add-log-modal-time-logged-input'
              }
            />
          </Suspense>
        </div>
      </div>
    </div>
  )

  const renderFlagForHandover = () => {
    return hasPro ? (
      <div
        style={styles.handoverContainer}
        data-testid={
          testId
            ? `${testId}-handover-container`
            : 'add-log-modal-handover-container'
        }
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={MODAL_ID}
            leftIcon="flag"
            keys="isNextHandover"
            label="Flag for handover?"
            type="singleCheckbox"
            values={formProps.fields}
            data-testid={
              testId
                ? `${testId}-handover-checkbox`
                : 'add-log-modal-handover-checkbox'
            }
          />
        </Suspense>
      </div>
    ) : null
  }

  if (
    !isOpen ||
    !categories ||
    !categoryItems ||
    !residents ||
    !summary ||
    !formProps.fields
  ) {
    return null
  }

  const {
    icon: { color, url },
    text
  } = summary

  // CARE-693: filter out hidden categories and items
  const visibleCategoryItems = _.filter(categoryItems, (d) => {
    if (d.isHidden) {
      return false
    }
    if (
      d.id === ITEM_ID_SHORT_SEIZURE &&
      flags?.clinical_safety_seizures.enabled
    ) {
      return false
    }
    return true
  })

  // hide category if all items hidden
  const visibleCategories = _.filter(categories, ({ id }) =>
    _.find(visibleCategoryItems, { category: id })
  )

  const { category } = formProps.fields
  const chosenItems = category && _.filter(visibleCategoryItems, { category })
  const chosenItem =
    formProps.fields &&
    formProps.fields.item &&
    _.find(visibleCategoryItems, { id: formProps.fields.item })
  const chosenResidents = filterResidentsByStatus(residents)
  const selectedResident = _.find(residents, {
    id: formProps.fields.resident
  })

  const onChangeSeizureType = (deEscalated: boolean) => {
    const itemId = deEscalated ? ITEM_ID_SHORT_SEIZURE : ITEM_ID_SEIZURE
    const categoryId = deEscalated ? HR_CATEGORY_ID : INCIDENT_CATEGORY_ID
    const title = categoryItems?.find((item) => item?.id === itemId)?.name

    formProps.setFormField({
      key: 'item',
      value: itemId
    })
    formProps.setFormField({
      key: 'category',
      value: categoryId
    })
    formProps.setFormField({
      key: 'title',
      value: title
    })
  }

  const isSeizure =
    formProps.fields.item === ITEM_ID_SEIZURE ||
    formProps.fields.item === ITEM_ID_SHORT_SEIZURE

  const showSeizureSwitch = flags?.clinical_safety_seizures.enabled && isSeizure

  const isSafeguarding = chosenItem?._id === SAFEGUARDING_ITEM_ID

  // If we open the modal from the task page, we have the information already collected.
  // Start from the final step.
  const hasRequiredData =
    formProps.fields.resident &&
    formProps.fields.category &&
    formProps.fields.item
  const initialStep =
    taskCompletion && hasRequiredData ? AddLogStep.LOG : AddLogStep.SU

  return (
    <div
      data-cy="create-log-item-container"
      data-testid={testId || 'add-log-modal'}
    >
      <StepList
        disableNavigation={taskCompletion}
        backIconColor={color}
        backIcon={url}
        backText={text}
        closeLabel="Cancel"
        headerProps={getHeaderProps()}
        onClose={close}
        initialStep={initialStep}
        data-testid={testId ? `${testId}-step-list` : 'add-log-modal-step-list'}
      >
        <Step
          component={ModalResidentSelector}
          data={{ residents: chosenResidents }}
          testId={
            testId
              ? `${testId}-resident-selector`
              : 'add-log-modal-resident-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalCategorySelector}
          allCategories
          data={{ categories: visibleCategories }}
          testId={
            testId
              ? `${testId}-category-selector`
              : 'add-log-modal-category-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalCategoryItemSelector}
          data={{ items: chosenItems, categories: visibleCategories }}
          testId={
            testId
              ? `${testId}-category-item-selector`
              : 'add-log-modal-category-item-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalTitleSelector}
          isValid={checkTitleValid}
          onSubmit={handleSubmit}
          formId={MODAL_ID}
          submitButtonText="Add Log"
          buttonGlyph="plus"
          item={chosenItem}
          validationText="Title and Description is required"
          stepHeader={renderDateTimePicker()}
          stepFooter={
            !isSafeguarding ? (
              <>
                <LogsGoalsSelect
                  residentID={selectedResident?.id}
                  onChange={(value) =>
                    formProps.setFormField({
                      key: 'goals',
                      value
                    })
                  }
                  data-testid={
                    testId
                      ? `${testId}-goals-select`
                      : 'add-log-modal-goals-select'
                  }
                />
                {showSeizureSwitch && (
                  <LogsSeizureDeEscalation
                    deEscalated={
                      formProps.fields.item === ITEM_ID_SHORT_SEIZURE
                    }
                    onChange={onChangeSeizureType}
                    data-testid={
                      testId
                        ? `${testId}-seizure-de-escalation`
                        : 'add-log-modal-seizure-de-escalation'
                    }
                  />
                )}
                {renderFlagForHandover()}
              </>
            ) : undefined
          }
          selectedResident={selectedResident}
          isLoading={isLoading}
          testId={
            testId ? `${testId}-title-selector` : 'add-log-modal-title-selector'
          }
          {...formProps}
        />
      </StepList>
      <ScrollLock />
    </div>
  )
}

const TIME = 'Time'
const PERMITTED_RESIDENT_STATUSES = [
  'active',
  'hospitalised',
  'released',
  'dummy'
]
const MODAL_ID = 'addLog'

const styles = {
  handoverContainer: {
    margin: 'auto',
    maxWidth: 400
  }
}

const classes = StyleSheet.create({
  timePickerContainer: {
    maxWidth: 400,
    margin: 'auto'
  },
  label: {
    textAlign: 'left',
    fontWeight: 600
  },
  formField: {
    paddingBottom: 15,
    maxWidth: 400
  },
  datePickerContainer: {
    marginTop: 4
  }
})

const mapStateToProps = (state: any, props: any) => ({
  categories: state.data['log-categories'],
  categoryItems: state.data['log-category-items'],
  hasPro: _.get(state, 'global.home.pro'),
  residents: state.data.residents,
  // @ts-ignore
  summary: Selectors.getTodoSummary(state, props)
})

const mapDispatchToProps = (dispatch: any) => ({
  fetchLogs: () =>
    dispatch(Operations.fetchLogs({ search: '', timeLogged: {} }))
})

const wrappedModal = connect(mapStateToProps, mapDispatchToProps)(AddLogModal)

export default Modal(wrappedModal, MODAL_ID)
